import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  ReactNode,
} from 'react';

interface HighlightedMessageContextProps {
  highlightedMessage: string | null;
  setHighlightedMessage: React.Dispatch<React.SetStateAction<string | null>>;
}

const HighlightedMessageContext = createContext<
  HighlightedMessageContextProps | undefined
>(undefined);

export const HighlightedMessageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [highlightedMessage, setHighlightedMessage] = useState<string | null>(
    null,
  );
  // Memoized so consumers that only need the (stable) setter don't re-render
  // the whole list + sidebar every time the highlight is set or cleared.
  const value = useMemo(
    () => ({ highlightedMessage, setHighlightedMessage }),
    [highlightedMessage],
  );

  return (
    <HighlightedMessageContext.Provider value={value}>
      {children}
    </HighlightedMessageContext.Provider>
  );
};

export const useHighlightedMessage = (): HighlightedMessageContextProps => {
  const context = useContext(HighlightedMessageContext);
  if (!context) {
    throw new Error(
      'useHighlightedMessage must be used within a HighlightedMessageProvider',
    );
  }
  return context;
};
